import { defineNuxtPlugin, useRouter, useRuntimeConfig } from "nuxt/app";
import { Analytics, type AnalyticsInterface } from "../service/analytics";
import { GoogleTagManager } from "../service/providers/google-tag-manager";
import { MixPanel } from "../service/providers/mixpanel";
import { Snowplow } from "../service/providers/snowplow";

export default defineNuxtPlugin(() => {
  const router = useRouter();
  const runtimeConfig = useRuntimeConfig();

  const analytics: AnalyticsInterface = import.meta.browser
    ? new Analytics()
    : {
        trackEvent: () => {},
        releaseQueue: () => {},
        addProvider: () => {},
        anonymize: () => {},
        setSuperProperties: () => {},
        identify: () => {},
      };

  router.isReady().then(() => {
    router.afterEach((to, from) => {
      analytics.trackEvent({
        name: "pageView",
        payload: {
          originRoutePath: from.fullPath,
          originRouteName: from.name?.toString(),
          targetRoutePath: to.fullPath,
          targetRouteName: to.name?.toString(),
        },
      });
    });
  });

  return {
    provide: {
      analytics: {
        trackEvent: analytics.trackEvent,
        releaseQueue: analytics.releaseQueue,
        setSuperProperties: analytics.setSuperProperties,
        identify: analytics.identify,
        anonymize: analytics.anonymize,

        enableSnowplow: () => {
          if (!runtimeConfig.public.analytics.snowplow.collector.length) return;

          analytics.addProvider(
            new Snowplow({
              collector: runtimeConfig.public.analytics.snowplow.collector,
              appId: runtimeConfig.public.analytics.snowplow.appId,
              schemaVendor:
                runtimeConfig.public.analytics.snowplow.schemaVendor,
            }),
          );
        },

        enableMixPanel: () => {
          if (!runtimeConfig.public.analytics.mixpanel.token.length) return;

          analytics.addProvider(
            new MixPanel({
              token: runtimeConfig.public.analytics.mixpanel.token,
              debug: runtimeConfig.public.analytics.mixpanel.debug,
            }),
          );
        },

        enableGoogleAnalytics: (
          config: ConstructorParameters<typeof GoogleTagManager>[0],
        ) => {
          analytics.addProvider(new GoogleTagManager(config));
        },
      },
    },
  };
});
